import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Icon,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  SimpleGrid,
  useDisclosure,
  useToast, // Import useToast
} from "@chakra-ui/react";
import {
  FaEdit,
  FaUsersCog,
  FaIdCard,
  FaHome,
  FaPhone,
  FaUser,
} from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const EditDriver = ({ staffId, RefreshData }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone_no: "",
    address: "",
    driving_license_no: "",
    staff_type: "",
  });

  const toast = useToast(); // Initialize the toast hook

  // Fetch staff details
  const fetchStaffDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/admin/bus/staff/view_bus_staff.php`,
        { params: { staff_id: staffId } }
      );
      console.log("view", response);

      if (response.data.success) {
        const { name, phone_no, address, driving_license_no, staff_type } =
          response.data.data;
        setFormData({
          name,
          phone_no,
          address,
          driving_license_no,
          staff_type,
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching staff details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch staff details.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle edit request
  const handleEdit = async () => {
    try {
      setLoading(true);
      const response = await axios.put(
        `${BASE_URL}/admin/bus/staff/edit_bus_staff.php`,
        {
          ...formData,
          user_id: sessionStorage.getItem("user_id"), // Replace with actual user ID
          school_id: sessionStorage.getItem("school_id"), // Replace with actual school ID
          id: staffId,
        }
      );

      console.log("edit", response);

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Bus staff details updated successfully!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
        RefreshData();
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error updating staff details:", error);
      toast({
        title: "Error",
        description: "Failed to update staff details.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Open modal and fetch details
  const handleOpenModal = () => {
    onOpen();
    fetchStaffDetails();
  };

  return (
    <>
      <IconButton
        icon={<FaEdit />}
        size="sm"
        colorScheme="blue"
        onClick={handleOpenModal}
        mr={2}
      />
      <Modal
        isCentered
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaEdit} boxSize={5} mr={2} />
            Edit Bus Staff
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>
                  <Icon as={FaUser} mr={1} color="teal.500" />
                  Name
                </FormLabel>
                <Input
                  name="name"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter name"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>
                  <Icon as={FaPhone} mr={1} color="teal.500" />
                  Phone Number
                </FormLabel>
                <Input
                  name="phone_no"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={formData.phone_no}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                />
              </FormControl>
              <FormControl isRequired mt={4}>
                <FormLabel>
                  <Icon as={FaHome} mr={1} color="teal.500" />
                  Address
                </FormLabel>
                <Input
                  name="address"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter address"
                />
              </FormControl>
              <FormControl isRequired mt={4}>
                <FormLabel>
                  <Icon as={FaIdCard} mr={1} color="teal.500" />
                  Driving License Number
                </FormLabel>
                <Input
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  name="driving_license_no"
                  value={formData.driving_license_no}
                  onChange={handleInputChange}
                  placeholder="Enter license number"
                />
              </FormControl>
              <FormControl isRequired mt={4}>
                <FormLabel>
                  <Icon as={FaUsersCog} mr={1} color="teal.500" />
                  Staff Type
                </FormLabel>
                <Input
                  name="staff_type"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  value={formData.staff_type}
                  onChange={handleInputChange}
                  placeholder="Enter staff type"
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            {/* <Button onClick={onClose} mr={3}>
              Cancel
            </Button> */}
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              onClick={handleEdit}
              isLoading={loading}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditDriver;
